import { type FocusEvent, memo, type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { type EditorView } from '@tiptap/pm/view';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { ArrowRightIcon, PlusSmallCircleIcon } from '@/icons';
import { showImageUploadModal } from '@/shared/features/image-upload/components';
import { prepareTiptapHTML } from '@/shared/features/rich-text';
import { getMatchItemVisibleText } from '@/shared/quiz/matchPairDisplay';
import { extractBareText } from '@/shared/utils/extractBareText';
import { Button, confirmDialog } from '@/ui_v3';
import { useImageUploadInput } from '@/views/build/hook/useImageUploadInput';
import { goToNextFocusable, goToPreviousFocusable } from '../../focus/focusChain';
import { focusField } from '../../focus/focusUtils';
import { useFocusTarget } from '../../focus/useFocusTarget';
import { useEditMode } from '../../focus/useIsEditMode';
import { DeleteButton } from '../DeleteButton';
import { EditableQuestion } from '../EditableQuestion';
import { EditTools } from '../EditTools';
import { HeightAnimate } from '../HeightAnimate';
import { useBuildTextEditor } from '../hook/useBuildTextEditor';
import { useDroppableImageUploadInput } from '../hook/useDroppableImageUploadInput';
import { usePasteFile } from '../hook/usePasteFile';
import { useQuizFormField } from '../hook/useQuizFormField';
import { MultipleOptionImage } from '../Multiple/MultipleOptionImage';
import { useQuizControl } from '../QuizControlProvider';
import { TextEditor } from '../TextEditor';
import { getDuplicateCountSignature, getDuplicateSignature } from './matchDuplicate';
import { type useMatchPairs } from './useMatchPairs';
type MatchColumn = 'leftItems' | 'rightItems';
export const getMatchFocusDirection = (key: string, shiftKey = false, isComposing = false) => {
  if (isComposing) return;
  if (key === 'ArrowDown' || key === 'Tab' && !shiftKey) return 'next';
  if (key === 'ArrowUp' || key === 'Tab' && shiftKey) return 'previous';
};
export const getAdjacentMatchFocusId = (quizIndex: number, column: MatchColumn, pairIndex: number, pairCount: number, reverse = false) => {
  const currentIndex = pairIndex * 2 + (column === 'rightItems' ? 1 : 0);
  const adjacentIndex = currentIndex + (reverse ? -1 : 1);
  if (adjacentIndex < 0 || adjacentIndex >= pairCount * 2) return;
  const adjacentColumn = adjacentIndex % 2 === 0 ? 'leftItems' : 'rightItems';
  const adjacentPairIndex = Math.floor(adjacentIndex / 2);
  return `quizList.${quizIndex}.matchData.${adjacentColumn}.${adjacentPairIndex}.text`;
};
export const shouldConfirmMatchDuplicateOnFocusChange = (currentFocusId: string, nextFocusId?: string) => Boolean(nextFocusId && nextFocusId !== currentFocusId && nextFocusId.includes('.matchData.') && nextFocusId.endsWith('.text'));
export const getMatchFocusIdAfterDuplicateConfirm = (confirmed: boolean, currentFocusId: string, nextFocusId?: string) => confirmed && nextFocusId ? nextFocusId : currentFocusId;

/**
 * 편집 중 중복 확인 팝업의 "이미 확인함" 상태는 필드가 아니라 퀴즈 카드 단위로
 * 공유되어야 한다. 같은 열의 어느 필드에서 편집하든 동일 중복 시그니처면 팝업을
 * 한 번만 띄우기 위해 EditableMatch 에서 ref 하나를 만들어 하위 입력에 전달한다.
 */
type ConfirmedDuplicateSignaturesRef = MutableRefObject<Set<string>>;
export const EditableMatch = memo(({
  pairs,
  handleAdd,
  handleRemove,
  canAdd,
  canRemove
}: ReturnType<typeof useMatchPairs>) => {
  const {
    isEditMode
  } = useEditMode();
  const {
    t
  } = useTranslation(['build']);
  const confirmedDuplicateSignaturesRef = useRef(new Set<string>());
  return <div className="flex flex-col gap-xs pb-xs" onClick={e => e.stopPropagation()}>
        <EditableQuestion />
        {isEditMode && <div className="rounded-sm border border-alternative py-2xs text-center text-caption-lg-semibold text-assistive">
            {t('build.match.shuffleHelper')}
          </div>}
        <div className="flex flex-col gap-2xs">
          {pairs.map((pair, pairIndex) => <EditableMatchPair key={pair.id} pairIndex={pairIndex} confirmedDuplicateSignaturesRef={confirmedDuplicateSignaturesRef} onRemove={canRemove && isEditMode ? () => handleRemove(pairIndex) : undefined} />)}
        </div>
        {canAdd && isEditMode && <div className="pt-xs">
            <Button variant="contained" intent="neutral" size="md" className="w-full" leftSlot={<PlusSmallCircleIcon size="xs" />} onClick={e => {
        e.stopPropagation();
        handleAdd();
      }}>
              {t('build.match.addPair')}
            </Button>
          </div>}
      </div>;
});
type EditableMatchPairProps = {
  pairIndex: number;
  confirmedDuplicateSignaturesRef: ConfirmedDuplicateSignaturesRef;
  onRemove?: () => void;
};
const EditableMatchPair = memo(({
  pairIndex,
  confirmedDuplicateSignaturesRef,
  onRemove
}: EditableMatchPairProps) => {
  const {
    t
  } = useTranslation(['build']);
  const ariaLabel = t('build.match.itemA11yLabel');
  return <div className="flex w-full items-start gap-xs">
        <div className="flex min-w-px flex-1 flex-wrap items-start gap-x-xs gap-y-[2px]">
          <div className="min-w-[72px] flex-1">
            <MatchTextInput column="leftItems" pairIndex={pairIndex} confirmedDuplicateSignaturesRef={confirmedDuplicateSignaturesRef} placeholder={t('build.match.leftPlaceholder', {
          index: pairIndex + 1
        })} ariaLabel={ariaLabel} />
          </div>
          <div className="flex justify-center pt-sm">
            <ArrowRightIcon size="xs" fill={ICON_COLOR.assistive} />
          </div>
          <div className="min-w-[96px] flex-1">
            <MatchTextInput column="rightItems" pairIndex={pairIndex} confirmedDuplicateSignaturesRef={confirmedDuplicateSignaturesRef} placeholder={t('build.match.rightPlaceholder', {
          index: pairIndex + 1
        })} ariaLabel={ariaLabel} />
          </div>
        </div>
        {onRemove && <div className="flex w-[30px] shrink-0 justify-center py-2xs">
            <DeleteButton onClick={onRemove} />
          </div>}
      </div>;
});
const MAX_MATCH_ITEM_TEXT_LENGTH = 50;
const MatchTextInput = ({
  column,
  pairIndex,
  confirmedDuplicateSignaturesRef,
  placeholder,
  ariaLabel
}: {
  column: MatchColumn;
  pairIndex: number;
  confirmedDuplicateSignaturesRef: ConfirmedDuplicateSignaturesRef;
  placeholder: string;
  ariaLabel: string;
}) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    control,
    index
  } = useQuizControl();
  const focusId = `quizList.${index}.matchData.${column}.${pairIndex}.text`;
  const previousRichTextRef = useRef('');
  const watchedColumnItems = useWatch({
    control,
    name: `quizList.${index}.matchData.${column}`
  });
  const columnItems = useMemo(() => watchedColumnItems ?? [], [watchedColumnItems]);
  const ref = useFocusTarget<HTMLDivElement>(focusId);
  const [text, updateText] = useQuizFormField({
    field: `matchData.${column}.${pairIndex}.text`
  });
  const [richText, updateRichText] = useQuizFormField({
    field: `matchData.${column}.${pairIndex}.richText`
  });
  const [equation, updateEquation] = useQuizFormField({
    field: `matchData.${column}.${pairIndex}.equation`
  });
  const [imageUrl, updateImageUrl] = useQuizFormField({
    field: `matchData.${column}.${pairIndex}.imageUrl`
  });
  const confirmDuplicate = useCallback(async (nextValue: string) => {
    const nextTexts = columnItems.map((item, itemIndex) => itemIndex === pairIndex ? nextValue : getMatchItemVisibleText(item));
    const duplicateSignature = getDuplicateSignature(nextTexts);
    const duplicateConfirmSignature = getDuplicateCountSignature(nextTexts);
    if (duplicateSignature && !confirmedDuplicateSignaturesRef.current.has(duplicateConfirmSignature)) {
      const confirmed = await confirmDialog.openAsync({
        header: {
          title: t('build.match.duplicate.title')
        },
        body: t('build.match.duplicate.body'),
        cancelText: t('build.match.duplicate.cancel'),
        confirmText: t('build.match.duplicate.confirm'),
        shouldCloseOnDimClick: false
      });
      if (!confirmed) return false;
      confirmedDuplicateSignaturesRef.current.add(duplicateConfirmSignature);
    }
    return true;
  }, [columnItems, confirmedDuplicateSignaturesRef, pairIndex, t]);
  useEffect(() => {
    previousRichTextRef.current = richText || (text ? prepareTiptapHTML(text) : equation ? prepareTiptapHTML(`$${equation}$`) : '');
  }, [equation, richText, text]);
  const {
    editor
  } = useBuildTextEditor({
    content: richText || (text ? prepareTiptapHTML(text) : equation ? prepareTiptapHTML(`$${equation}$`) : ''),
    onUpdate: content => {
      const nextText = extractBareText(content);
      if (nextText.length > MAX_MATCH_ITEM_TEXT_LENGTH) {
        editor?.commands.setContent(previousRichTextRef.current);
        return;
      }
      previousRichTextRef.current = content;
      updateText(nextText);
      updateRichText(content);
      updateEquation(undefined);
    }
  });
  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget;
    const nextFocusId = relatedTarget instanceof Element ? relatedTarget.closest<HTMLElement>('[data-focus-id]')?.getAttribute('data-focus-id') ?? undefined : undefined;
    if (!shouldConfirmMatchDuplicateOnFocusChange(focusId, nextFocusId)) {
      return;
    }
    void confirmDuplicate(extractBareText(editor?.getHTML() ?? '')).then(confirmed => {
      focusField(getMatchFocusIdAfterDuplicateConfirm(confirmed, focusId, nextFocusId));
    });
  }, [confirmDuplicate, editor, focusId]);
  const handleKeyDown = useCallback((_: EditorView, event: KeyboardEvent) => {
    const direction = getMatchFocusDirection(event.key, event.shiftKey, event.isComposing);
    if (!direction) return;
    const adjacentFocusId = getAdjacentMatchFocusId(index, column, pairIndex, columnItems.length, direction === 'previous');
    event.preventDefault();
    (direction === 'previous' ? goToPreviousFocusable : goToNextFocusable)({
      current: focusId,
      to: adjacentFocusId
    });
    return true;
  }, [column, columnItems.length, focusId, index, pairIndex]);
  editor?.setOptions({
    editorProps: {
      handleKeyDown
    }
  });
  const {
    handleFileChange,
    accept,
    isUploading
  } = useImageUploadInput({
    onUploadComplete: nextImageUrl => {
      updateImageUrl(nextImageUrl);
    }
  });
  const handleFileChangeVoid = (file: File | null) => {
    void handleFileChange(file);
  };
  const {
    rootProps,
    inputProps,
    isDragActive,
    inputRef
  } = useDroppableImageUploadInput({
    onFileChange: handleFileChangeVoid,
    accept,
    imageUrl,
    disabled: false
  });
  usePasteFile({
    editor,
    shouldConfirm: !!imageUrl,
    onPaste: handleFileChangeVoid
  });
  return <div className="group flex w-full flex-col gap-2xs" {...rootProps} data-sentry-component="MatchTextInput" data-sentry-source-file="EditableMatch.tsx">
      <HeightAnimate className="relative" data-sentry-element="HeightAnimate" data-sentry-source-file="EditableMatch.tsx">
        <input {...inputProps} className="hidden" ref={inputRef} />
        <TextEditor.Input ref={ref} editor={editor} placeholder={placeholder} aria-label={ariaLabel} placeholderClassName="px-3xs text-heading-sm-bold" editorClassName="text-heading-sm-bold" onBlur={handleBlur} className={isDragActive ? 'px-3xs py-2xs text-heading-sm-bold text-default shadow-[inset_0_-2px_0_0_theme(colors.violet.300)]' : 'px-3xs py-2xs text-heading-sm-bold text-default'} onClick={e => {
        e.stopPropagation();
      }} data-sentry-element="unknown" data-sentry-source-file="EditableMatch.tsx" />
        <EditTools.InlineTooltipContainer className="hidden group-focus-within:flex" data-sentry-element="unknown" data-sentry-source-file="EditableMatch.tsx">
          <TextEditor.EquationButton editor={editor} className="hidden mobile-lg:flex" data-sentry-element="unknown" data-sentry-source-file="EditableMatch.tsx" />
          <EditTools.Divider className="hidden mobile-lg:flex" data-sentry-element="unknown" data-sentry-source-file="EditableMatch.tsx" />
          <EditTools.ImageAddButton onClick={() => {
          showImageUploadModal({
            place: 'build',
            onUploadStart: handleFileChangeVoid,
            imageUrl
          });
        }} data-sentry-element="unknown" data-sentry-source-file="EditableMatch.tsx" />
        </EditTools.InlineTooltipContainer>
      </HeightAnimate>
      {imageUrl && <MultipleOptionImage imageUrl={imageUrl} isUploading={isUploading} className="mb-sm ml-3xs" onRemove={() => updateImageUrl(undefined)} />}
    </div>;
};